import { useCallback, useEffect, useRef, useState } from "react";
import { Building2, Download, FileSpreadsheet, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { DataPanel } from "@/components/CommandSystem";

const MIGRATION_MODES = {
  full: {
    label: "Entire workbook",
    shortLabel: "Full",
    description: "Import clients, hoardings, campaigns, links, expenses, schedules, invoices, payments, and payables.",
    templateEndpoint: "/admin/data-migration/template",
    previewEndpoint: "/admin/data-migration/preview",
    uploadEndpoint: "/admin/data-migration/upload",
    templateName: "hoardexa_data_migration_template.xlsx",
    fileLabel: "Completed full Excel workbook",
    counts: [
      ["Clients", "clients"],
      ["Hoardings", "hoardings"],
      ["Campaigns", "campaigns"],
      ["Links", "campaign-hoardings", "campaign_hoardings"],
      ["Expenses", "expenses"],
      ["Schedules", "schedules"],
    ],
  },
  hoardings: {
    label: "Hoardings only",
    shortLabel: "Hoardings",
    description: "Import only hoarding inventory data from a workbook containing README, Branches, and Hoardings.",
    templateEndpoint: "/admin/data-migration/template/hoardings",
    previewEndpoint: "/admin/data-migration/preview/hoardings",
    uploadEndpoint: "/admin/data-migration/upload/hoardings",
    templateName: "hoardexa_hoardings_import_template.xlsx",
    fileLabel: "Completed hoardings Excel file",
    counts: [
      ["Hoardings", "hoardings"],
      ["Branches", "branches"],
    ],
  },
  clients: {
    label: "Clients only",
    shortLabel: "Clients",
    description: "Import only client data from a workbook containing README, Branches, and Clients.",
    templateEndpoint: "/admin/data-migration/template/clients",
    previewEndpoint: "/admin/data-migration/preview/clients",
    uploadEndpoint: "/admin/data-migration/upload/clients",
    templateName: "hoardexa_clients_import_template.xlsx",
    fileLabel: "Completed clients Excel file",
    counts: [
      ["Clients", "clients"],
      ["Branches", "branches"],
    ],
  },
};

export function DataMigration() {
  const [status, setStatus] = useState(null);
  const [advertisers, setAdvertisers] = useState([]);
  const [advertiserId, setAdvertiserId] = useState("");
  const [migrationMode, setMigrationMode] = useState("full");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirmCompany, setConfirmCompany] = useState("");
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const inputRef = useRef(null);

  const fetchStatus = useCallback(() => {
    if (!advertiserId) {
      setStatus(null);
      return;
    }
    api.get("/admin/data-migration/status", {
      params: { advertiser_id: advertiserId },
      skipGlobalLoader: true,
    })
      .then(({ data }) => setStatus(data))
      .catch(() => {});
  }, [advertiserId]);

  useEffect(() => {
    api.get("/admin/advertisers", { skipGlobalLoader: true })
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        setAdvertisers(list);
        setAdvertiserId((current) => current || list[0]?.id || "");
      })
      .catch(() => toast.error("Could not load advertisers"));
  }, []);

  useEffect(() => {
    fetchStatus();
    const timer = window.setInterval(fetchStatus, status?.active ? 5000 : 15000);
    return () => window.clearInterval(timer);
  }, [fetchStatus, status?.active]);

  const downloadTemplate = async () => {
    const mode = MIGRATION_MODES[migrationMode];
    try {
      const { data } = await api.get(mode.templateEndpoint, { responseType: "blob" });
      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = mode.templateName;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Template download failed");
    }
  };

  const upload = async () => {
    const mode = MIGRATION_MODES[migrationMode];
    if (!advertiserId) {
      toast.error("Select advertiser first.");
      return;
    }
    if (!file) {
      toast.error("Select the completed Excel workbook first.");
      return;
    }
    const body = new FormData();
    body.append("advertiser_id", advertiserId);
    body.append("confirm_company", confirmCompany);
    body.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post(mode.uploadEndpoint, body);
      toast.success(data?.message || "Migration started");
      setFile(null);
      setPreview(null);
      setConfirmCompany("");
      if (inputRef.current) inputRef.current.value = "";
      fetchStatus();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const previewWorkbook = async () => {
    const mode = MIGRATION_MODES[migrationMode];
    if (!advertiserId) {
      toast.error("Select advertiser first.");
      return;
    }
    if (!file) {
      toast.error("Select the completed Excel workbook first.");
      return;
    }
    const body = new FormData();
    body.append("advertiser_id", advertiserId);
    body.append("file", file);
    setPreviewing(true);
    try {
      const { data } = await api.post(mode.previewEndpoint, body);
      setPreview(data);
      setConfirmCompany("");
      toast.success("Workbook preview ready");
    } catch (e) {
      setPreview(null);
      toast.error(formatApiError(e.response?.data?.detail) || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const counts = status?.counts || {};
  const active = Boolean(status?.active);
  const selectedAdvertiser = advertisers.find((advertiser) => advertiser.id === advertiserId);
  const selectedCompany = selectedAdvertiser?.company_name || "";
  const confirmed = selectedCompany && confirmCompany.trim().toLowerCase() === selectedCompany.trim().toLowerCase();
  const mode = MIGRATION_MODES[migrationMode];
  const previewCountRows = mode.counts.map(([label, previewKey]) => [label, preview?.counts?.[previewKey] || 0]);
  const statusCountRows = mode.counts.map(([label, previewKey, statusKey]) => [label, counts[statusKey || previewKey] || 0]);

  return (
    <Layout
      title="Data Migration"
      subtitle="Import a completed Excel workbook into a selected advertiser account."
    >
      <div className="space-y-5 max-w-5xl">
        <DataPanel title="Advertiser account" subtitle="Choose which advertiser receives the imported records.">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-blue-50 p-2 text-blue-700">
              <Building2 size={18} />
            </div>
            <div className="flex-1">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Advertiser account</span>
                <select
                  value={advertiserId}
                  onChange={(e) => {
                    setAdvertiserId(e.target.value);
                    setStatus(null);
                    setFile(null);
                    setPreview(null);
                    setConfirmCompany("");
                    if (inputRef.current) inputRef.current.value = "";
                  }}
                  className="mt-2 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  <option value="">Select advertiser</option>
                  {advertisers.map((advertiser) => (
                    <option key={advertiser.id} value={advertiser.id}>
                      {advertiser.company_name || advertiser.owner_name || advertiser.email}
                    </option>
                  ))}
                </select>
              </label>
              <p className="mt-2 text-xs text-slate-500">
                The selected advertiser will receive all imported clients, hoardings, campaigns, expenses, and schedules.
              </p>
            </div>
          </div>
        </DataPanel>

        <DataPanel title="Import type" subtitle="Choose whether this upload is the full migration workbook or one focused data file.">
          <div className="grid gap-3 md:grid-cols-3">
            {Object.entries(MIGRATION_MODES).map(([key, item]) => (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setMigrationMode(key);
                  setFile(null);
                  setPreview(null);
                  setConfirmCompany("");
                  if (inputRef.current) inputRef.current.value = "";
                }}
                className={`rounded-lg border p-4 text-left transition ${migrationMode === key ? "border-blue-500 bg-blue-50 shadow-sm" : "border-slate-200 bg-white hover:border-blue-200"}`}
              >
                <div className="text-sm font-bold text-slate-950">{item.label}</div>
                <p className="mt-1 text-xs leading-5 text-slate-500">{item.description}</p>
              </button>
            ))}
          </div>
        </DataPanel>

        <DataPanel
          title={`${mode.label} migration workbook`}
          subtitle={`Use the matching Excel format for this import type.${selectedAdvertiser ? ` Current target: ${selectedAdvertiser.company_name}.` : ""}`}
          actions={
            <button
              type="button"
              onClick={downloadTemplate}
              className="hx-secondary-action"
            >
              <Download size={15} />
              Download Template
            </button>
          }
        >
          <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Completed Excel file</span>
              <span className="mt-1 block text-sm font-medium text-slate-700">{mode.fileLabel}</span>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx"
                disabled={!advertiserId || active || uploading}
                onChange={(e) => {
                  setFile(e.target.files?.[0] || null);
                  setPreview(null);
                  setConfirmCompany("");
                }}
                className="mt-2 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
            <button
              type="button"
              disabled={!advertiserId || !file || active || uploading || previewing}
              onClick={previewWorkbook}
              className="hx-secondary-action disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FileSpreadsheet size={15} />
              {previewing ? "Previewing..." : "Preview Excel"}
            </button>
          </div>
        </DataPanel>

        {preview && (
          <section className="rounded-lg border border-amber-200 bg-amber-50 p-5 shadow-sm">
            <div className="flex flex-col gap-4">
              <div>
                <div className="text-sm font-semibold text-amber-950">Confirm migration target</div>
                <p className="mt-1 text-sm text-amber-800">
                  This Excel will import into <b>{selectedCompany}</b>. Type the company name below to unlock upload.
                </p>
                <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-amber-700">
                  Import type: {mode.label}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                {previewCountRows.map(([label, value]) => (
                  <div key={label} className="rounded-md border border-amber-200 bg-white/70 p-3">
                    <div className="text-xs font-semibold uppercase text-amber-700">{label}</div>
                    <div className="mt-1 text-lg font-bold text-amber-950">{value || 0}</div>
                  </div>
                ))}
              </div>
              {preview.warnings?.length > 0 && (
                <div className="rounded-md bg-white p-3 text-sm text-amber-900">
                  <div className="font-semibold">Warnings before upload</div>
                  <ul className="mt-1 list-disc space-y-1 pl-5">
                    {preview.warnings.map((warning, index) => <li key={index}>{warning}</li>)}
                  </ul>
                </div>
              )}
              <label className="block">
                <span className="text-sm font-semibold text-amber-950">Type advertiser company name</span>
                <input
                  value={confirmCompany}
                  onChange={(e) => setConfirmCompany(e.target.value)}
                  placeholder={selectedCompany}
                  className="mt-2 block w-full rounded-md border border-amber-300 px-3 py-2 text-sm"
                />
              </label>
              <button
                type="button"
                disabled={!advertiserId || !file || !preview || !confirmed || active || uploading}
                onClick={upload}
                className="hx-primary-action w-fit disabled:cursor-not-allowed disabled:opacity-50"
              >
                <UploadCloud size={16} />
                {uploading ? "Uploading..." : `Upload ${mode.shortLabel}`}
              </button>
            </div>
          </section>
        )}

        <DataPanel
          title="Latest migration status"
          subtitle={!advertiserId ? "Select an advertiser to view migration status." : status?.message || "No migration has been run yet."}
          actions={
            <span className={`w-fit rounded-full px-3 py-1 text-xs font-semibold ${active ? "bg-amber-100 text-amber-800" : status?.status === "failed" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>
              {status?.status || "idle"}
            </span>
          }
        >
          {active && typeof status?.progress === "number" && (
            <div className="mb-4">
              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-blue-600 transition-[width] duration-300"
                  style={{ width: `${Math.min(100, Math.max(0, status.progress))}%` }}
                />
              </div>
              <div className="mt-1 text-right text-xs font-medium text-slate-500">{Math.round(status.progress)}%</div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {statusCountRows.map(([label, value]) => (
              <div key={label} className="rounded-md border border-slate-200 p-3">
                <div className="text-xs font-semibold uppercase text-slate-500">{label}</div>
                <div className="mt-1 text-lg font-bold text-slate-950">{value || 0}</div>
              </div>
            ))}
          </div>
          {status?.errors?.length > 0 && (
            <div className="mt-4 rounded-md bg-rose-50 p-3 text-sm text-rose-700">
              {status.errors[0]}
            </div>
          )}
        </DataPanel>
      </div>
    </Layout>
  );
}
